import React, {
  useMemo, useRef, useState,
  useLayoutEffect, useEffect,
} from 'react';
import { Box, Typography, styled, useTheme } from '@mui/material';
import Container from '@mui/material/Container';

interface Chapter {
  id: number;
  title: string;
  image?: string;
  locked: boolean;
}

export default function LearningJourney({
  chapters,
  completedIds,
  onSelectChapter,
  courseTitle
}: {
  chapters: Chapter[];
  completedIds: number[];
  onSelectChapter?: (id: number) => void;
  courseTitle?: string; 
}) {
  const theme = useTheme();
  const svgRef = useRef<SVGSVGElement | null>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [mid, setMid] = useState<number | null>(null);
  const [isWide, setIsWide] = useState(false);


  useEffect(() => {
    const update = () => setIsWide(window.innerWidth >= 1024);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  // Variablen Werte für Desktop/Mobile
  const OFFSET = isWide ? 180 : 60;          
  const ICON_HEIGHT = isWide ? 56 : 72;       
  const gapY = isWide ? 80 : 100;            
  const TRAIL_WIDTH = isWide ? 160 : 120;     
  const FONT_SIZE = isWide ? '1rem' : '0.8rem';

  // Berechnung Y-Positionen
  const ys = chapters.map((_, i) => i * gapY + ICON_HEIGHT / 2);


  useLayoutEffect(() => {
    if (svgRef.current) {
      const width = svgRef.current.getBoundingClientRect().width;
      setMid(width / 2 + 0);
    }
  }, [isWide]);

  // Trail X-Positionen 
  const getTrailXCenters = () =>
    chapters.map((_, i) => (mid ?? 0) + (i % 2 === 0 ? -OFFSET : OFFSET));

  // Fortschrittsberechnung
  const normalizedCompletedIds = useMemo(
    () => completedIds.map((id) => Number(id)),
    [completedIds]
  );
  const lastDone = (() => {
    for (let i = chapters.length - 1; i >= 0; i--) {
      if (normalizedCompletedIds.includes(Number(chapters[i].id))) return i;
    }
    return -1;
  })();

  // SVG-Path bauen
  const buildPath = (ys: number[], xs: number[]) => {
    if (!mid || ys.length < 2 || xs.length !== ys.length) return '';
    let d = `M${xs[0]},${ys[0]} `;
    const controlDistance = isWide ? 60 : 40;
    for (let i = 1; i < ys.length; i++) {
      const prevX = xs[i - 1];
      const prevY = ys[i - 1];
      const targetX = xs[i];
      const targetY = ys[i];
      d += `C ${prevX},${prevY + controlDistance} ${targetX},${targetY - controlDistance} ${targetX},${targetY} `;
    }
    return d;
  };

  const trailXCenters = getTrailXCenters();
  const fullPath = useMemo(() => {
    if (ys.length < 2 || !mid) return '';
    return buildPath(ys, trailXCenters);
  }, [ys, trailXCenters, mid]);

  // Fortschritts-Animation
  const doneRef = useRef<SVGPathElement | null>(null);
  const hasAnimated = useRef(false);

  useLayoutEffect(() => {
    if (!doneRef.current || !mid) return;
    if (ys.length < 2) return;
    const fullLen = doneRef.current.getTotalLength();
    const donePx = lastDone <= 0 ? 0 : (lastDone * fullLen) / (ys.length - 1);

    doneRef.current.setAttribute('stroke-dasharray', `${donePx} ${fullLen}`);
    doneRef.current.setAttribute('stroke-dashoffset', donePx.toString());

    if (!hasAnimated.current) {
      requestAnimationFrame(() => {
        if (!doneRef.current) return;
        doneRef.current.style.transition = 'stroke-dashoffset 1200ms ease-in-out';
        doneRef.current.style.strokeDashoffset = '0';
        hasAnimated.current = true;
      });
    }
  }, [mid, lastDone, ys, trailXCenters]);

  // Eintrag
  const TrailItem = styled('div')<{
    top: number;
    left: number;
    unlocked: boolean;
  }>(({ top, left }) => ({
    position: 'absolute',
    top,
    left,
    transform: 'translate(-50%, -28px)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 10,
    width: TRAIL_WIDTH,
    textAlign: 'center',
    cursor: 'pointer',
  }));

  // Container (Desktop maxWidth = md, Mobile volle Breite)
  return (
    <Container maxWidth={isWide ? "md" : false}>
      <Box sx={{
        position: 'relative',
        pt: isWide ? 8 : 4,
        pb: isWide ? 8 : 8 + 50, // Extra Puffer auf Mobile (BottomBar)
        minHeight: ys.at(-1)! + ICON_HEIGHT + (isWide ? 60 : 80),
      }}>
        {/* SVG Pfade */}
        <svg
          ref={svgRef}
          width="100%"
          height={Math.max(...ys) + ICON_HEIGHT + (isWide ? 60 : 80)}
          style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'visible' }}
        >
          {/* Hintergrundpfad (gestrichelt, hellgrau) */}
          <path
            d={fullPath}
            fill="none"
            stroke={theme.palette.grey[100]}
            strokeWidth={isWide ? 4 : 4}
            strokeLinecap="round"
            strokeDasharray={isWide ? "8 18" : "6 10"}
          />
          {/* Fortschrittspfad (primär, durchgezogen) */}
          <path
            ref={doneRef}
            d={fullPath}
            fill="none"
            stroke={theme.palette.primary.main}
            strokeWidth={isWide ? 4 : 4}
            strokeLinecap="round"
            strokeDasharray="none"
          />
        </svg>

        {/* Kapitel */}
        {chapters.map((chapter, i) => {
          const isExtension = chapter.id === -1 || chapter.id === -2;
          const isCompleted = isExtension || normalizedCompletedIds.includes(Number(chapter.id));

          return (
            <TrailItem
              key={chapter.id}
              ref={(el) => (itemRefs.current[i] = el)}
              top={ys[i]}
              left={trailXCenters[i]}
              unlocked={isCompleted}
              onClick={() => onSelectChapter?.(chapter.id)}
            >
              {chapter.image && (
                <img
                  src={chapter.image}
                  alt={chapter.title}
                  width={ICON_HEIGHT}
                  height={ICON_HEIGHT}
                  style={{
                    filter: isCompleted ? 'none' : 'grayscale(100%) opacity(0.7)',
                    transition: 'transform 0.2s ease',
                    cursor: 'pointer',
                  }}
                  onMouseOver={e => (e.currentTarget.style.transform = 'scale(1.08)')}
                  onMouseOut={e => (e.currentTarget.style.transform = 'scale(1)')}
                />
              )}
              <Typography
                sx={{
                  mt: 1,
                  maxWidth: TRAIL_WIDTH,
                  fontWeight: isExtension ? 700 : isCompleted ? 700 : 300,
                  color: isCompleted ? theme.palette.text.primary : theme.palette.grey[500],
                  fontSize: FONT_SIZE,
                  lineHeight: 1.2,
                  textAlign: 'center',
                  whiteSpace: 'normal',
                  overflow: 'visible',
                  textOverflow: 'ellipsis',
                  cursor: 'pointer',
                  transition: 'opacity 0.2s ease',
                  '&:hover': { opacity: 0.8 },
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {chapter.title}
              </Typography>
            </TrailItem>
          );
        })}
      </Box>
    </Container>
  );
}
